import { useData } from '@/components';
import { useSelectedStore } from '@/hooks';
import {
  isCurbsideAllowedForStore,
  isGuestCheckoutAllowedForStore,
  isMedAndRecSelectionActiveForStore,
  isMedicalDocumentRequiredForStore,
  isMedicalSignUpAllowedForStore,
} from '@/utils';
interface IUseFeaturedFlagResponse {
  isDiscountsList: boolean;
  isGRLimitActive: boolean;
  showStriketroughPrice: boolean;
  showRegulationModal: boolean;
  disableStoreRewards: boolean;
  keepShoppingActive: boolean;
  isTaxAppliedMessage: boolean;
  disableProductReviews: boolean;
  isSingleHeroBanner: string | undefined;
  wishlistActive: boolean;
  isMedAndRecSelectionActive: boolean;
  isAddToCartInKioskDisabled: boolean;
  isSurfsideActive: boolean;
  showProductWarning: boolean;
  isStartUpPlan: boolean;
  allowCancelOrder: boolean;
  allowIdVerifiationDelivery: boolean;
  allowIdVerifiationPickup: boolean;
  forceToSelectStore: boolean;
  forceToSelectStoreOnEachVisit: boolean;
  allowMedicalSignUps: boolean;
  requireStateOfResidence: boolean;
  allowCurbsidePickup: boolean;
  isCurbsideAllowedForStore: (_shortName?: string | null) => boolean;
  allowGuestCheckout: boolean;
  curbsidePickupPlaceholder: string;
  checkoutCountDownTime: number;
  allowCouponLoyaltyStacking: boolean;
  enableMMIDTooltip: boolean;
  medicalDocumentRequired: boolean;
  hideMap: boolean;
  paymentDetailNoteDelivery: string;
  paymentDetailNotePickup: string;
}

const useFeaturedFlag = (): IUseFeaturedFlagResponse => {
  const { store } = useSelectedStore();
  const { treezStores } = useData();

  const isWishlistActive =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_WISH_LIST_ACTIVE ?? 'false';

  const isSingleHeroBanner =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_SINGLE_HERO_BANNER;

  const isDiscountsList =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_DISCOUNTS_LIST_ACTIVE;

  const isGRLimitActive = process.env.NEXT_PUBLIC_FEATURE_FLAG_GRLIMIT_ACTIVE;

  const showStriketroughPrice =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_SHOW_STRIKETROUGH_PRICE ?? 'true';

  const disableStoreRewards =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_DISABLE_STORE_REWARDS ?? 'false';

  const keepShoppingActive =
    process.env.NEXT_PUBLIC_KEEP_SHOPPING_BUTTON_ACTIVE ?? 'false';

  const disableProductReviews = true;

  const isAddToCartInKioskDisabled =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_DISABLE_ADD_TO_CART_IN_KIOSK ??
    'false';

  const surfsideActive = process.env.NEXT_PUBLIC_SURFSIDE_ACTIVE ?? 'false';

  const showProductWarning =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_P65_WARNING ?? 'true';

  const isStartUpPlan =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_IS_STARTUP_PLAN ?? 'false';

  const allowCancelOrder =
    process.env.NEXT_PUBLIC_ALLOW_CANCEL_ORDER_FROM_CONFIRMATION_PAGE ?? 'true';

  const allowIdVerifiationDelivery =
    process.env.NEXT_PUBLIC_ALLOW_ID_VERIFICATION_FOR_DELIVERY ?? 'false';

  const allowIdVerifiationPickup =
    process.env.NEXT_PUBLIC_ALLOW_ID_VERIFICATION_FOR_PICKUP ?? 'false';

  const forceToSelectStore =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_FORCE_TO_SELECT_STORE ?? 'false';

  const forceToSelectStoreOnEachVisit =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_FORCE_TO_SELECT_STORE_VISIT_EACH ??
    'false';

  const showRegulation =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_SB_540_REGULATION ?? 'false';

  const requireStateOfResidence =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_REQUIRE_STATE_AT_CHECKOUT ?? 'false';

  const curbsidePickupPlaceholder =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_CURBSIDE_PICKUP_PLACEHOLDER ?? '';

  const checkoutCountDownTime =
    Number(process.env.NEXT_PUBLIC_FEATURE_FLAG_CHECKOUT_COUNTDOWN_TIME) || 10;

  const allowCouponLoyaltyStacking =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_ALLOW_DISCOUNT_LOYALTY_STACKING ??
    'true';

  const enableMMIDTooltip =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_ENABLE_MMID_TOOLTIP ?? 'false';

  const hideMapFlag =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_HIDE_MAP_AT_CHECKOUT ?? 'false';

  const paymentDetailNoteDelivery =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_PAYMENT_DETAIL_NOTE_DELIVERY ?? '';

  const paymentDetailNotePickup =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_PAYMENT_DETAIL_NOTE_PICKUP ?? '';

  const isCurbsidePickupAllowed = isCurbsideAllowedForStore(store?.short_name);

  const isGuestCheckoutAllowed = isGuestCheckoutAllowedForStore(
    store?.short_name,
  );

  const isMedAndRecSelectionActive = store?.short_name
    ? isMedAndRecSelectionActiveForStore(store.short_name)
    : !!treezStores?.length &&
      treezStores.every(treezStore =>
        isMedAndRecSelectionActiveForStore(treezStore?.short_name),
      );

  const isMedicalDocumentRequired = isMedicalDocumentRequiredForStore(
    store?.short_name,
  );

  const isMedicalSignUpAllowed = isMedicalSignUpAllowedForStore(
    store?.short_name,
  );

  // display_post_tax is deprecated, use post_tax instead
  const isPostTax = store?.post_tax ?? store?.display_post_tax ?? false;

  return {
    isDiscountsList: isDiscountsList === 'true',
    isGRLimitActive: isGRLimitActive === 'true',
    showStriketroughPrice: showStriketroughPrice === 'true',
    disableStoreRewards: disableStoreRewards === 'true',
    keepShoppingActive: keepShoppingActive === 'true',
    disableProductReviews,
    isTaxAppliedMessage: isPostTax,
    isSingleHeroBanner,
    wishlistActive: isWishlistActive === 'true',
    isMedAndRecSelectionActive,
    isAddToCartInKioskDisabled: isAddToCartInKioskDisabled === 'true',
    isSurfsideActive: surfsideActive === 'true',
    showProductWarning: showProductWarning === 'true',
    isStartUpPlan: isStartUpPlan === 'true',
    allowCancelOrder: allowCancelOrder === 'true',
    allowIdVerifiationDelivery: allowIdVerifiationDelivery === 'true',
    allowIdVerifiationPickup: allowIdVerifiationPickup === 'true',
    forceToSelectStore: forceToSelectStore === 'true',
    forceToSelectStoreOnEachVisit: forceToSelectStoreOnEachVisit === 'true',
    showRegulationModal: showRegulation === 'true',
    allowMedicalSignUps: isMedicalSignUpAllowed,
    requireStateOfResidence: requireStateOfResidence === 'true',
    allowCurbsidePickup: isCurbsidePickupAllowed,
    isCurbsideAllowedForStore,
    allowGuestCheckout: isGuestCheckoutAllowed,
    curbsidePickupPlaceholder,
    checkoutCountDownTime,
    allowCouponLoyaltyStacking: allowCouponLoyaltyStacking === 'true',
    enableMMIDTooltip: enableMMIDTooltip === 'true',
    medicalDocumentRequired: isMedicalDocumentRequired,
    hideMap: hideMapFlag === 'true',
    paymentDetailNoteDelivery,
    paymentDetailNotePickup,
  };
};

export { useFeaturedFlag };
